export const CURRENT_VERSION = "V1.17.2";

export type VersionEntry = {
  version: string;
  date: string;
  title: string;
  summary: string[];
};

export const VERSION_HISTORY: VersionEntry[] = [
  {
    version: "V1.17.2",
    date: "2026-04-28",
    title: "Pilot Health dashboard + Solo referral nudge",
    summary: [
      "New manager-only Pilot Health page (sidebar → Pilot Health) with DAU/WAU, deals & activities last 7 days, push opt-in rate, and a per-rep usage table that flags anyone who hasn't touched the app in 48h.",
      "Solo workspaces now get a friendly 'Invite a teammate' nudge in Settings after closing 3+ deals — sending the invite auto-converts the workspace from solo to multi-seat.",
      "Pilot Health link is hidden for solo accounts; team management UI stays focused on dealerships only.",
    ],
  },
  {
    version: "V1.17.1",
    date: "2026-04-28",
    title: "Push notifications",
    summary: [
      "New Settings → Push Notifications card: one-tap to enable on any device. Per-event toggles for stall alerts, hot-deal flags, and red CSI ratings.",
      "iPhone users get a friendly 'Add to Home Screen first' banner — iOS only supports push for installed PWAs.",
      "Database triggers automatically push when a deal goes Hot or a customer leaves a red CSI rating; goes to the rep + all managers/admins on that dealership.",
      "Subscribing also auto-completes the 'Enable notifications' onboarding step.",
      "Built on standard Web Push (VAPID) with no third-party push provider — works in Chrome, Edge, Firefox, Android, and iOS 16.4+ Safari (when installed).",
    ],
  },
  {
    version: "V1.17",
    date: "2026-04-28",
    title: "Pilot validation: onboarding + telemetry",
    summary: [
      "New 60-second onboarding checklist on the rep and manager dashboards: create your first deal, log an activity, set a next step, check the leaderboard, enable notifications. Auto-checks itself off as you go and can be dismissed.",
      "Lightweight in-app product analytics now capture signups, logins, deal creates, activities logged, next steps set, and leaderboard views — so we can finally see who's actually using PullOver day-to-day.",
      "Database triggers automatically mark onboarding milestones the moment a deal or activity is created — no extra clicks needed.",
      "Foundation for the V1.18 push-notifications + Pilot Health dashboard rollout.",
    ],
  },
  {
    version: "V1.16",
    date: "2026-04-28",
    title: "Solo rep sign-up",
    summary: [
      "Any sales rep can now sign up without a parent dealership — they get a private 'Solo Workspace' auto-created at signup, named '{First Name}'s Workspace'.",
      "Solo reps land on the rep dashboard, get the same hot-deal flagging, CSI automation, and end-of-day digest email as a full dealership team.",
      "All team-management UI (invite reps, pilot user list, team-wide views) is hidden in solo workspaces — clean single-person experience.",
      "New Settings → Your Solo Workspace card lets reps rename their workspace anytime.",
      "Pilot dealerships (JLR Waterloo, etc.) continue to work exactly as before — only non-pilot signups become solo.",
    ],
  },
  {
    version: "V1.15",
    date: "2026-04-28",
    title: "Automated CSI feedback requests",
    summary: [
      "Customers now get a branded one-tap CSI request email 24 hours after a deal is marked Sold — Green / Yellow / Red, no login required.",
      "Public feedback page at /csi/:token captures the rating (and optional notes) and writes it straight into the dealership's CSI feed and leaderboard.",
      "Hourly cron job picks up newly sold deals, skips anyone who already rated or was already emailed, and only sends if the customer has an email on file.",
      "One-time tokens expire after 30 days and are single-use, so links are safe to share via email.",
    ],
  },
  {
    version: "V1.14",
    date: "2026-04-28",
    title: "Auto hot-deal flagging",
    summary: [
      "Deals are now automatically flagged as Hot when 3 or more activities are logged within 48 hours — no manual toggling required.",
      "When a deal is moved to Sold, the Hot flag is automatically cleared so the manager dashboard stays focused on live opportunities.",
      "Runs at the database level, so the badge stays accurate across reps, devices, and sessions in real time.",
    ],
  },
  {
    version: "V1.13",
    date: "2026-04-28",
    title: "Manager daily digest email",
    summary: [
      "New end-of-day digest email sent to every dealership manager at 6 PM Eastern: deals created, demos logged, appointments set, deals sold, CSI breakdown, top rep, stalled deals, and deals missing a next step.",
      "Branded email template (Pull Over Sales red accent, Inter font) with a one-click link back to the dashboard.",
      "Compliant unsubscribe page at /unsubscribe — managers can opt out anytime; suppression is automatic.",
      "Skipped silently on days with zero floor activity, so inboxes stay clean during weekends and slow days.",
    ],
  },
  {
    version: "V1.12",
    date: "2026-04-28",
    title: "Stall alerts & next-step enforcement",
    summary: [
      "Every new deal now requires a next step at creation — no more silent 'call' default. Reps pick from Call, Follow-up, Appointment, Send Numbers, Trade Review, or Close.",
      "Stage cannot advance past New (toward Sold) until a next step is set — the app blocks the change and prompts the rep.",
      "24-hour stall alerts are now derived live from each deal's last activity: under 24h = active, 24–48h = slowing, over 48h = no activity. No cron required.",
      "Manager Alerts panel now sorts stalled deals by oldest activity first, so the worst offenders bubble to the top.",
    ],
  },
  {
    version: "V1.11",
    date: "2026-04-28",
    title: "Pilot go-live: real-mode persistence & team invites",
    summary: [
      "Demo Mode fully separated from JLR Waterloo — generic 'My Dealership' branding, sample inventory, and visible Demo badge across all authenticated pages",
      "Real Mode now persists end-to-end: Quick Log writes to activities and updates parent deal, CSI ratings save to customer_feedback, and Leaderboard scores compute live from the database",
      "Manager Dashboard now lists every rep on the team — even before their first deal — by reading directly from user_roles",
      "New Invite Team Member dialog in Settings: managers can add reps or managers by email; new users receive a branded invite to set their password and join the dealership automatically",
    ],
  },
  {
    version: "V1.10",
    date: "2026-04-17",
    title: "UI polish",
    summary: [
      "Rebranded 'PullOverSales' to 'Pull Over Sales' across landing, settings, sidebar, meta tags, and PWA manifest",
      "Refined landing hero copy and 'Why Dealerships' heading with red accent on Pull/Sales",
      "Enabled clean white-label experience across the app",
      "Added subtle version label next to Sign Out and Version History panel in Settings",
    ],
  },
  {
    version: "V1",
    date: "2026-04-17",
    title: "Initial Release",
    summary: [
      "Core dashboard layout created",
      "Rep workflow and manager visibility structure established",
      "Hero section and branding direction added",
      "Initial pipeline, activity, and lead tracking concept defined",
    ],
  },
];
